import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

interface NavigationProps {
    className?: string;
}

export const Navigation: React.FC<NavigationProps> = ({ className = '' }) => {
    const { isAuthenticated, user } = useAuth();
    const location = useLocation();
    const [pendingCount, setPendingCount] = useState<number | null>(null);

    // Check if user is admin (handle both boolean and numeric values)
    const isAdmin = user?.is_admin === true || user?.is_admin === 1;

    useEffect(() => {
        // Only fetch pending count if user is authenticated AND is admin
        if (isAuthenticated && isAdmin) {
            fetchPendingCount();
        } else {
            // Clear the count for non-admin users
            setPendingCount(null);
        }
    }, [isAuthenticated, user, isAdmin]);

    // Close mobile menu when location changes

    const fetchPendingCount = async () => {
        // Double-check that user is admin before making the request
        if (!isAdmin) {
            setPendingCount(null);
            return;
        }

        try {
            const response = await fetch('/api/suggestions/admin/pending', {
                credentials: 'include'
            });

            if (response.ok) {
                const suggestions = await response.json();
                setPendingCount(suggestions.length);
            } else {
                setPendingCount(null);
            }
        } catch (error) {
            console.error('Error fetching pending count:', error);
            setPendingCount(null);
        }
    };

    const navLinkClass = (isActive: boolean) =>
        `font-bold transition-colors block py-2 px-3 rounded-md ${isActive
            ? 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20'
            : 'text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
        }`;

    const navItems: Array<{
        to: string;
        label: string;
        mobileLabel?: string;
        isActive: boolean;
        isExternal?: boolean;
        hideOnMobile?: boolean;
    }> = [
        // Core navigation - shown on both desktop and mobile
        ...(isAuthenticated ? [
            {
                to: '/favourites',
                label: 'Favourites',
                isActive: location.pathname === '/favourites',
                hideOnMobile: false
            },
            {
                to: '/watchlist',
                label: 'Watchlist',
                mobileLabel: 'List',
                isActive: location.pathname === '/watchlist',
                hideOnMobile: false
            }
        ] : []),
        {
            to: '/channels',
            label: 'Channels',
            mobileLabel: 'Chan',
            isActive: location.pathname === '/channels',
            hideOnMobile: false
        },
        {
            to: '/genres',
            label: 'Genres',
            isActive: location.pathname === '/genres',
            hideOnMobile: false
        },
        {
            to: 'mailto:manhwarecap.dev@gmail.com',
            label: 'Contact',
            isActive: false,
            isExternal: true,
            hideOnMobile: true // Hide contact on mobile to save space
        },
        ...(isAuthenticated && isAdmin ? [
            {
                to: '/admin',
                label: `Admin${pendingCount !== null && pendingCount > 0 ? `(${pendingCount})` : ''}`,
                mobileLabel: 'Admin',
                isActive: location.pathname.startsWith('/admin'),
                hideOnMobile: false
            }
        ] : [])
    ];

    return (
        <div className={`relative ${className}`}>
            {/* Responsive navigation */}
            <nav className="flex items-center space-x-1 sm:space-x-2 md:space-x-3 lg:space-x-4" style={{display: 'flex', flexDirection: 'row'}}>
                {navItems.map((item) => {
                    const linkClass = `${navLinkClass(item.isActive)} whitespace-nowrap text-xs sm:text-sm ${
                        item.hideOnMobile ? 'hidden sm:block' : 'block'
                    }`;

                    if (item.isExternal) {
                        return (
                            <a
                                key={item.to}
                                href={item.to}
                                className={linkClass}
                            >
                                {item.mobileLabel || item.label}
                            </a>
                        );
                    }
                    return (
                        <Link
                            key={item.to}
                            to={item.to}
                            className={linkClass}
                        >
                            {item.mobileLabel || item.label}
                        </Link>
                    );
                })}
            </nav>
        </div>
    );
}; 